import React, { useState } from "react";
import { Box, Typography, Stack, IconButton, Divider, Tooltip } from "@mui/material";
import PersonIcon from "@mui/icons-material/PersonOutline";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import BookOutlinedIcon from "@mui/icons-material/BookOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReduceCapacityOutlinedIcon from "@mui/icons-material/ReduceCapacityOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccountOutlined";
import AccountTreeIcon from "@mui/icons-material/AccountTreeOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUpOutlined";
import EmojiEventsIcon from "@mui/icons-material/EmojiEventsOutlined";
import LayersIcon from "@mui/icons-material/LayersOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { WorkspacePremium, AccountTree } from "@mui/icons-material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import dayjs from "dayjs";
import { MonthPicker } from "../../../components/ui/MonthPicker";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import TrendingDownIcon from "@mui/icons-material/TrendingDownOutlined";
import SegmentOutlinedIcon from "@mui/icons-material/SegmentOutlined";
import { SectionHeader } from "./SectionHeader";

// ─── Shared card atom ────────────────────────────────────────────────────────
function StatCard({ label, value, Icon, iconBg, iconColor, accentColor, prefix = "", suffix = "", stats, href, sx: sxProp }) {
	const isPositive = typeof stats === "string" && stats.startsWith("+");
	const isNegative = typeof stats === "string" && stats.startsWith("-");

	const handleClick = () => {
		if (href) window.location.href = href;
	};

	return (
		<Box
			onClick={handleClick}
			sx={{
				flex: "1 1 0",
				width: "100%",
				minWidth: 0,
				minHeight: { xs: 80, sm: 100 },
				position: "relative",
				display: "flex",
				alignItems: "center",
				gap: { xs: 1.5, sm: 2 },
				backgroundColor: "#fff",
				borderRadius: 2,
				p: { xs: "1rem 1.125rem", sm: "1.25rem 1.5rem" },
				borderRight: `4px solid ${accentColor}`,
				boxShadow: "0 2px 8px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.06)",
				transition: "box-shadow 0.2s ease",
				cursor: href ? "pointer" : "default",
				userSelect: "none",
				"&:hover": {
					boxShadow: "0 4px 16px rgba(0,0,0,0.13), 0 0 1px rgba(0,0,0,0.08)",
				},
				"&:active": href
					? {
							transform: "scale(0.98)",
							boxShadow: "0 2px 8px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.06)",
						}
					: {},
				...sxProp,
			}}
		>
			{/* Top-right stats badge */}
			{stats && (
				<Box
					sx={{
						position: "absolute",
						top: 8,
						right: 12,
						display: "flex",
						alignItems: "center",
						gap: 0.25,
					}}
				>
					{isPositive && <TrendingUpIcon sx={{ fontSize: 13, color: "#2E7D32" }} />}
					{isNegative && <TrendingDownIcon sx={{ fontSize: 13, color: "#C62828" }} />}
					<Typography
						sx={{
							fontSize: 11,
							fontWeight: 700,
							lineHeight: 1,
							color: isPositive ? "#2E7D32" : isNegative ? "#C62828" : "text.secondary",
						}}
					>
						{stats} this mo.
					</Typography>
				</Box>
			)}

			<Box
				sx={{
					width: { xs: 40, sm: 48 },
					height: { xs: 40, sm: 48 },
					borderRadius: 2,
					backgroundColor: iconBg,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
				}}
			>
				<Icon sx={{ fontSize: { xs: 20, sm: 24 }, color: iconColor }} />
			</Box>
			<Box sx={{ minWidth: 0 }}>
				<Typography
					sx={{
						fontSize: { xs: 11, sm: 13 },
						color: "text.secondary",
						lineHeight: 1.4,
						mb: 0.25,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{label}
				</Typography>
				<Typography
					sx={{
						fontSize: { xs: 22, sm: 28 },
						fontWeight: 700,
						color: "#1D54A2",
						lineHeight: 1.1,
					}}
				>
					{prefix}
					{typeof value === "number" ? value.toLocaleString() : value}
					{suffix}
				</Typography>
			</Box>
		</Box>
	);
}

// ─── Row of cards — wraps on mobile ──────────────────────────────────────────
function CardRow({ cards }) {
	return (
		<Box
			sx={{
				display: "flex",
				flexWrap: { xs: "wrap", md: "nowrap" },
				gap: 2,
				width: "100%",
			}}
		>
			{cards.map((card) => (
				<StatCard
					key={card.label}
					{...card}
					sx={{
						flexBasis: { xs: "100%", sm: "calc(50% - 8px)", md: 0 },
						flexGrow: 1,
					}}
				/>
			))}
		</Box>
	);
}

// ─── Qualification Status Card ────────────────────────────────────────────────
function QualificationStatusCard({ qualification_status, override_status, is_activated = true, has_period_data = true, period_label = "", href }) {
	const overrideKey = override_status?.toLowerCase() ?? "inactive";
	// Nothing imported for this cutoff yet — say so rather than reporting a status
	// derived from absent data, which reads as "opted out" and misleads.
	const noData = has_period_data === false;
	// A deactivated agent has no override or qualification to speak of — they're
	// excluded from every computation, so show that instead of two empty states.
	const optedOut = !noData && is_activated === false;

	const handleClick = () => {
		if (href) window.location.href = href;
	};

	const overrideStatusConfig = {
		active: {
			Icon: CheckCircleIcon,
			iconBg: "#E8F5E9",
			iconColor: "#2E7D32",
			accentColor: "#2E7D32",
			badge: { bg: "#E8F5E9", color: "#2E7D32" },
		},
		pending: {
			Icon: HourglassEmptyIcon,
			iconBg: "#FFF8E1",
			iconColor: "#F57F17",
			accentColor: "#FBC02D",
			badge: { bg: "#FFF8E1", color: "#F57F17" },
		},
		inactive: {
			Icon: CancelIcon,
			iconBg: "#FFEBEE",
			iconColor: "#C62828",
			accentColor: "#C62828",
			badge: { bg: "#FFEBEE", color: "#C62828" },
		},
		// Absence of data isn't a failure state, so it reads neutral rather than red.
		no_data: {
			Icon: HourglassEmptyIcon,
			iconBg: "#F1F3F5",
			iconColor: "#6B7280",
			accentColor: "#B0BEC5",
			badge: { bg: "#F1F3F5", color: "#4B5563" },
		},
	};

	const qualificationBadgeConfig = {
		passed: { bg: "#E8F5E9", color: "#2E7D32" },
		in_progress: { bg: "#FFF8E1", color: "#F57F17" },
		not_started: { bg: "#FFEBEE", color: "#C62828" },
	};

	const config = noData
		? overrideStatusConfig.no_data
		: optedOut
			? overrideStatusConfig.inactive
			: (overrideStatusConfig[overrideKey] ?? overrideStatusConfig.inactive);
	const qualKey = qualification_status?.toLowerCase() ?? "not_started";
	const qualBadge = qualificationBadgeConfig[qualKey] ?? { bg: "#F5F5F5", color: "#616161" };

	const formatLabel = (val) => val?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) ?? "";

	const BadgeColumn = ({ label, value, badge }) => (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
			<Typography
				sx={{
					fontSize: { xs: 11, sm: 13 },
					color: "text.secondary",
					lineHeight: 1.4,
					mb: 0.25,
					whiteSpace: "nowrap",
					overflow: "hidden",
					textOverflow: "ellipsis",
				}}
			>
				{label}
			</Typography>
			<Box
				sx={{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					backgroundColor: badge.bg,
					borderRadius: 99,
					px: 1.75,
					py: 0.6,
					minWidth: 80,
				}}
			>
				<Typography
					sx={{
						fontSize: 13,
						fontWeight: 600,
						color: badge.color,
						lineHeight: 1,
						whiteSpace: "nowrap",
					}}
				>
					{formatLabel(value)}
				</Typography>
			</Box>
		</Box>
	);

	return (
		<Box
			onClick={handleClick}
			sx={{
				flex: "1 1 0",
				width: "100%",
				minHeight: { xs: 80, sm: 100 },
				display: "flex",
				alignItems: "center",
				gap: { xs: 1.5, sm: 2 },
				backgroundColor: "#fff",
				borderRadius: 2,
				p: { xs: "1rem 1.125rem", sm: "1.25rem 1.5rem" },
				borderRight: `4px solid ${config.accentColor}`,
				boxShadow: "0 2px 8px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.06)",
				transition: "box-shadow 0.2s ease",
				cursor: href ? "pointer" : "default",
				userSelect: "none",
				"&:hover": {
					boxShadow: "0 4px 16px rgba(0,0,0,0.13), 0 0 1px rgba(0,0,0,0.08)",
				},
				"&:active": href
					? {
							transform: "scale(0.98)",
							boxShadow: "0 2px 8px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.06)",
						}
					: {},
			}}
		>
			{/* Status icon — driven by override */}
			<Box
				sx={{
					width: { xs: 40, sm: 48 },
					height: { xs: 40, sm: 48 },
					borderRadius: 2,
					backgroundColor: config.iconBg,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
				}}
			>
				<config.Icon sx={{ fontSize: { xs: 20, sm: 24 }, color: config.iconColor }} />
			</Box>

			{/* Side-by-side badges, or a single no-data / opted-out state */}
			{noData ? (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
					<BadgeColumn label={period_label || "This period"} value="No Data Imported" badge={config.badge} />
					<Typography sx={{ fontSize: 11, color: "text.disabled", lineHeight: 1.3 }}>Try adjusting the month in the summary section.</Typography>
				</Box>
			) : optedOut ? (
				<BadgeColumn label="Service" value="Opted Out" badge={config.badge} />
			) : (
				<Stack direction="row" gap={2} alignItems="flex-start">
					<BadgeColumn label="Override" value={override_status} badge={config.badge} />
					<Divider orientation="vertical" flexItem sx={{ borderColor: "#F0F4F8" }} />
					<BadgeColumn label="Qualification" value={qualification_status} badge={qualBadge} />
				</Stack>
			)}
		</Box>
	);
}

// ─── Checkout Line Item ───────────────────────────────────────────────────────
function CheckoutLineItem({
	icon: Icon,
	iconBg,
	iconColor,
	label,
	value,
	prefix = "$",
	actionLabel,
	onAction,
	isClickable,
	helpText,
	cardbgColor,
	description,
	valueTitle,
	isNegative = false,
}) {
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: { xs: "flex-start", sm: "center" },
				justifyContent: "space-between",
				borderRadius: 2,
				borderLeft: `4px solid ${iconColor}`,
				boxShadow: "0 2px 8px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.06)",
				backgroundColor: `${cardbgColor}`,
				p: { xs: 1.5, sm: 2 },
				gap: { xs: 1, sm: 1 },
				mb: 2,
			}}
		>
			{/* Left: Icon + Label */}
			<Box sx={{ display: "flex", alignItems: "flex-start", gap: { xs: 1.25, sm: 2 }, minWidth: 0, flex: { sm: 1, md: 0.7 } }}>
				<Box
					sx={{
						width: { xs: 44, sm: 64 },
						height: { xs: 44, sm: 64 },
						borderRadius: 1.5,
						backgroundColor: iconBg,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
					}}
				>
					<Icon sx={{ fontSize: { xs: 22, sm: 30 }, color: iconColor }} />
				</Box>

				{/* Label + optional help icon */}
				<Box sx={{ minWidth: 0, flex: 1 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
						<Typography
							variant="body2"
							sx={{
								fontSize: { xs: 13, sm: 15 },
								color: "text.secondary",
								lineHeight: 1.3,
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								fontWeight: 700,
							}}
						>
							{label}
						</Typography>
						{helpText && (
							<Tooltip
								title={helpText}
								placement="top"
								arrow
								enterDelay={100}
								componentsProps={{
									tooltip: {
										sx: {
											bgcolor: "#1a2332",
											color: "#CBD5E1",
											fontSize: 11.5,
											fontWeight: 400,
											maxWidth: 220,
											lineHeight: 1.5,
											px: 1.25,
											py: 0.75,
											borderRadius: 1,
											boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
											"& .MuiTooltip-arrow": { color: "#1a2332" },
										},
									},
								}}
							>
								<IconButton
									disableRipple
									sx={{
										ml: 0.5,
										p: 0,
										backgroundColor: "transparent !important",
										color: "text.disabled",
										flexShrink: 0,
										"&:hover": { color: "#3A7DC9", backgroundColor: "transparent" },
										transition: "color 0.15s ease",
									}}
								>
									<HelpOutlineOutlinedIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
								</IconButton>
							</Tooltip>
						)}
					</Box>
					<Typography
						variant="caption"
						sx={{
							color: "text.secondary",
							lineHeight: 1.4,
							whiteSpace: "normal",
							wordBreak: "break-word",
							fontSize: { xs: 11, sm: 12 },
						}}
					>
						{description}
					</Typography>
				</Box>
			</Box>

			{/* Right: Value + action */}
			<Box
				sx={{
					display: "flex",
					alignItems: "flex-end",
					gap: 1.5,
					flexShrink: 0,
					flexDirection: "column",
					alignSelf: { xs: "center", sm: "auto" },
				}}
			>
				<Stack gap={0.5} alignItems="flex-end !important">
					<Typography
						textAlign="end"
						sx={{
							fontSize: { xs: 9, sm: 11 },
							fontWeight: 500,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
						}}
					>
						{valueTitle}
					</Typography>
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.15 }}>
						<Stack direction="row" alignItems="center" gap={0.5} mb={0.5}>
							{isNegative ? (
								<RemoveOutlinedIcon sx={{ fontSize: { xs: 18, sm: 25 }, color: iconColor }} />
							) : (
								<AddOutlinedIcon sx={{ fontSize: { xs: 18, sm: 25 }, color: iconColor }} />
							)}
							<Typography
								sx={{
									fontSize: { xs: 18, sm: 28 },
									fontWeight: 700,
									color: iconColor,
									lineHeight: 1,
								}}
							>
								{prefix}
								{typeof value === "number" ? value.toLocaleString() : value}
							</Typography>
						</Stack>

						{isClickable && (
							<Box
								onClick={onAction}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.5,
									cursor: "pointer",
									backgroundColor: "#E6F1FB",
									borderRadius: 1,
									px: { xs: 0.75, sm: 1 },
									py: 0.5,
									transition: "all 0.18s ease",
									"&:hover": {
										backgroundColor: "#1D54A2",
										"& .breakdown-text, & .breakdown-icon": { color: "#fff" },
									},
								}}
							>
								<Typography
									className="breakdown-text"
									sx={{
										fontSize: { xs: 11, sm: 12 },
										fontWeight: 600,
										color: "#1D54A2",
										lineHeight: 1,
										transition: "color 0.18s ease",
									}}
								>
									{actionLabel}
								</Typography>
								<ChevronRightIcon
									className="breakdown-icon"
									sx={{ fontSize: { xs: 12, sm: 14 }, color: "#1D54A2", transition: "color 0.18s ease" }}
								/>
							</Box>
						)}
					</Box>
				</Stack>
			</Box>
		</Box>
	);
}

// ─── Override Breakdown Panel (slide-in) ─────────────────────────────────────
function OverrideBreakdownPanel({
	override_l1,
	override_l2,
	override_l3,
	override_total,
	onClose,
	override_percentage,
	leadership_bonus,
	network_residue_total_income,
	milestone_achieved,
}) {
	const overrideLevels = [
		{
			label: "Level 1",
			value: override_l1,
			accentColor: "#00ACC1",
			iconColor: "#00838F",
			iconBg: "#E0F7FA",
			percent: override_percentage?.l1_percent,
		},
		{
			label: "Level 2",
			value: override_l2,
			accentColor: "#1E88E5",
			iconColor: "#1565C0",
			iconBg: "#E3F2FD",
			percent: override_percentage?.l2_percent,
		},
		{
			label: "Level 3",
			value: override_l3,
			accentColor: "#8E24AA",
			iconColor: "#6A1B9A",
			iconBg: "#F3E5F5",
			percent: override_percentage?.l3_percent,
		},
	];

	return (
		<Box
			sx={{
				position: "absolute",
				top: 0,
				left: 0,
				width: "100%",
				height: "100%",
				backgroundColor: "#fff",
				borderRadius: 3,
				display: "flex",
				flexDirection: "column",
				zIndex: 2,
				animation: "slideInFromRight 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
				"@keyframes slideInFromRight": {
					from: { transform: "translateX(100%)", opacity: 0 },
					to: { transform: "translateX(0)", opacity: 1 },
				},
			}}
		>
			{/* Panel header */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					px: 2.5,
					pt: 2.5,
					pb: 1.5,
					borderBottom: "1px solid #F0F4F8",
				}}
			>
				<Stack direction="row" alignItems="center" spacing={1}>
					<Box
						sx={{
							width: 40,
							height: 40,
							borderRadius: 1.5,
							backgroundColor: "#E6F1FB",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<SegmentOutlinedIcon sx={{ fontSize: 20, color: "#1D54A2" }} />
					</Box>
					<Typography sx={{ fontSize: 19, fontWeight: 700, color: "#1D54A2" }}>Income Breakdown</Typography>
				</Stack>
				<IconButton
					onClick={onClose}
					sx={{
						backgroundColor: "transparent !important",
						"&:hover": { backgroundColor: "#E6F1FB" },
						width: 38,
						height: 38,
					}}
				>
					<ChevronLeftIcon sx={{ fontSize: 28, color: "#1D54A2" }} />
				</IconButton>
			</Box>

			{/* Scrollable content */}
			<Box sx={{ flex: 1, px: 2.5, py: 1, overflowY: "auto" }}>
				{/* Override sub-header */}
				<Typography
					sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", letterSpacing: 0.8, textTransform: "uppercase", pt: 1, pb: 0.5 }}
				>
					Overrides
				</Typography>

				{/* Override level rows */}
				{overrideLevels.map((lvl, i) => {
					const isPositive = typeof lvl.percent === "string" && lvl.percent.startsWith("+");
					const isNegative = typeof lvl.percent === "string" && lvl.percent.startsWith("-");

					return (
						<Box key={lvl.label}>
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5 }}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
									<Box
										sx={{
											width: 42,
											height: 42,
											borderRadius: 1.5,
											backgroundColor: lvl.iconBg,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										<LayersIcon sx={{ fontSize: 19, color: lvl.iconColor }} />
									</Box>
									<Box>
										<Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.2 }}>Override</Typography>
										<Typography sx={{ fontSize: 15, fontWeight: 600, color: "#1a2b4a", lineHeight: 1.2 }}>{lvl.label}</Typography>
									</Box>
								</Box>

								<Stack direction="row" alignItems="center" spacing={1}>
									{lvl.percent && (
										<Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
											{isPositive && <TrendingUpIcon sx={{ fontSize: 11, color: "#2E7D32" }} />}
											{isNegative && <TrendingDownIcon sx={{ fontSize: 11, color: "#C62828" }} />}
											<Typography
												sx={{
													fontSize: 10,
													fontWeight: 700,
													lineHeight: 1,
													color: isPositive ? "#2E7D32" : isNegative ? "#C62828" : "text.secondary",
												}}
											>
												{lvl.percent} this mo.
											</Typography>
										</Box>
									)}
									<Box sx={{ textAlign: "right" }}>
										<Typography sx={{ fontSize: 16, fontWeight: 700, color: "#1D54A2" }}>
											${lvl.value.toLocaleString()}
										</Typography>
										<Box
											sx={{
												width: "100%",
												height: 3,
												borderRadius: 99,
												backgroundColor: lvl.accentColor,
												mt: 0.5,
												opacity: 0.7,
											}}
										/>
									</Box>
								</Stack>
							</Box>
							{i < overrideLevels.length - 1 && <Divider sx={{ borderColor: "#F0F4F8" }} />}
						</Box>
					);
				})}

				{/* Override subtotal */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						backgroundColor: "#F0F4F8",
						borderRadius: 1.5,
						px: 2,
						py: 1.25,
						mt: 0.5,
						mb: 2,
					}}
				>
					<Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1a2b4a" }}>Override Subtotal</Typography>
					<Typography sx={{ fontSize: 15, fontWeight: 700, color: "#1D54A2" }}>${override_total.toLocaleString()}</Typography>
				</Box>

				<Divider sx={{ borderColor: "#F0F4F8", mb: 1.5 }} />

				{/* Leadership Bonus row */}
				{leadership_bonus > 0 && Array.isArray(milestone_achieved) && milestone_achieved.length > 0 && (
					<>
						<Typography
							sx={{
								fontSize: 11,
								fontWeight: 700,
								color: "text.secondary",
								letterSpacing: 0.8,
								textTransform: "uppercase",
								pb: 0.5,
								mt: 2,
							}}
						>
							Leadership bonus
						</Typography>

						{milestone_achieved.map((m, i) => {
							const statusLabel = m.status?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
							return (
								<Box key={m.id ?? i}>
									<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5 }}>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
											<Box
												sx={{
													width: 42,
													height: 42,
													borderRadius: 1.5,
													backgroundColor: "#FFF8E1",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
												}}
											>
												<EmojiEventsIcon sx={{ fontSize: 19, color: "#F9A825" }} />
											</Box>
											<Box>
												<Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.2 }}>Milestone</Typography>
												<Typography sx={{ fontSize: 15, fontWeight: 600, color: "#1a2b4a", lineHeight: 1.2 }}>
													{m.target} Members
												</Typography>
											</Box>
										</Box>

										<Box sx={{ textAlign: "right" }}>
											<Typography sx={{ fontSize: 16, fontWeight: 700, color: "#1D54A2" }}>
												${parseFloat(m.reward || 0).toLocaleString()}
											</Typography>
											<Box
												sx={{
													width: "100%",
													height: 3,
													borderRadius: 99,
													backgroundColor: "#F9A825",
													mt: 0.5,
													opacity: 0.7,
												}}
											/>
										</Box>
									</Box>
									{i < milestone_achieved.length - 1 && <Divider sx={{ borderColor: "#F0F4F8" }} />}
								</Box>
							);
						})}
					</>
				)}
			</Box>

			{/* Grand Total */}
			<Box
				sx={{
					mx: 1,
					mb: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					backgroundColor: "#1D54A2",
					borderRadius: 2,
					px: 2,
					py: 2,
					boxShadow: "0 2px 8px rgba(29,84,162,0.25)",
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
					<Box
						sx={{
							width: 28,
							height: 28,
							borderRadius: 1.5,
							backgroundColor: "rgba(255,255,255,0.15)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<TrendingUpIcon sx={{ fontSize: 15, color: "#fff" }} />
					</Box>
					<Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.85)", fontWeight: 500 }}>Total Income</Typography>
				</Box>

				<Typography sx={{ fontSize: 18, fontWeight: 700, color: "#fff" }}>${network_residue_total_income.toLocaleString()}</Typography>
			</Box>
		</Box>
	);
}

// ─── Total Checkout Box ───────────────────────────────────────────────────────
function TotalCheckoutBox({
	monthly_service_revenue,
	override_total,
	override_l1,
	override_l2,
	override_l3,
	total_agent_checkout,
	leadership_bonus,
	title,
	month,
	override_percentage,
	active_members,
	network_residue_total_income,
	milestone_achieved,
	isAgent,
}) {
	const [showBreakdown, setShowBreakdown] = useState(false);

	const netAmount = network_residue_total_income - monthly_service_revenue;
	const isProfit = netAmount > 0;
	const isBalanced = netAmount === 0;

	const totalRowConfig = isProfit
		? {
				bg: "#1D54A2",
				statusLabel: "NET PAYOUT",
				title: "Net Monthly Payout",
				subtitle: isAgent ? "Your earnings after fees for this month." : "Agent earnings after fees for this month.",
				StatusIcon: CheckCircleOutlineOutlinedIcon,
				statusText: "Ready for Payout",
				statusBg: "rgba(56, 142, 60, 0.3)",
				statusBorder: "rgba(76, 175, 80, 0.55)",
				statusIconColor: "#A5D6A7",
				statusTextColor: "#A5D6A7",
			}
		: isBalanced
			? {
					bg: "#1D54A2",
					statusLabel: "NET BALANCE",
					title: "Net Monthly Balance",
					subtitle: isAgent ? "You're all settled up for this month." : "Agent is settled up for this month.",
					StatusIcon: CheckCircleOutlineOutlinedIcon,
					statusText: "No payment needed",
					statusBg: "rgba(21, 101, 192, 0.35)",
					statusBorder: "rgba(100, 181, 246, 0.5)",
					statusIconColor: "#90CAF9",
					statusTextColor: "#EBF5FF",
				}
			: {
					bg: "#D9740E",
					statusLabel: "NET PAYABLE",
					title: "Net Monthly Payable",
					subtitle: "Amount due for this month.",
					StatusIcon: HourglassEmptyOutlinedIcon,
					statusText: isAgent ? "Waiting for your payment" : "Waiting for agent payment",
					statusBg: "rgba(255, 255, 255, 0.18)",
					statusBorder: "rgba(255, 224, 178, 0.6)",
					statusIconColor: "#FFE0B2",
					statusTextColor: "#FFE0B2",
				};

	return (
		<Box flex={{ xs: 1, lg: 1 }} width="100%">
			<SectionHeader icon={ReceiptLongIcon} title={title} />

			{/* Checkout card */}
			<Box
				sx={{
					position: "relative",
					backgroundColor: "#fff",
					borderRadius: 2,
					boxShadow: "0 2px 12px rgba(0,0,0,0.09), 0 0 1px rgba(0,0,0,0.06)",
					overflow: "hidden",
					border: "1px solid #EEF2F7",
				}}
			>
				{/* Receipt icon watermark */}
				<Box
					sx={{
						position: "absolute",
						top: -10,
						right: -10,
						opacity: 0.04,
						pointerEvents: "none",
					}}
				>
					<ReceiptLongIcon sx={{ fontSize: 160, color: "#1D54A2" }} />
				</Box>
				{/* Main checkout panel */}
				<Box sx={{ px: 2.5, pt: 2, pb: 0, height: "fit-content" }}>
					{/* Header row */}
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
						<Typography sx={{ fontSize: 20, fontWeight: 700, color: "#8FA3B8", letterSpacing: "0.08em", textTransform: "uppercase" }}>
							Summary
						</Typography>
					</Box>

					<Stack direction={"row"} alignItems={"center !important"} gap={2} my={3.5} ml={1.5}>
						<Typography variant="h5" sx={{ fontWeight: 700 }}>
							Month of
						</Typography>
						{month}
					</Stack>

					{/* Monthly Overrides Total line — clickable breakdown */}
					<CheckoutLineItem
						icon={ReduceCapacityOutlinedIcon}
						iconBg="#E8F4EC"
						iconColor="#2E9B57"
						label="Network Residual Income"
						value={network_residue_total_income}
						isClickable
						actionLabel="View Breakdown"
						helpText="This is income earned by you from your downline production."
						onAction={() => setShowBreakdown(true)}
						cardbgColor={"#FAFDFA"}
						description={"Residual earnings generated from your qualified network"}
						valueTitle={"TOTAL EARNINGS"}
					/>

					<Divider sx={{ borderColor: "#F0F4F8" }} />

					{/* Monthly Service Revenue line */}
					<CheckoutLineItem
						icon={BookOutlinedIcon}
						iconBg="#F8F0E3"
						iconColor="#E68A00"
						label="Personal Book Service Fees"
						value={monthly_service_revenue}
						helpText={`These fees are charged against your personal book of business active members. ( ${active_members} x $3 )`}
						cardbgColor={"#FEFCF9"}
						description={"Administrative and servicing fees associated with your active personal book."}
						valueTitle={"TOTAL FEES CHARGED"}
						isNegative={true}
					/>
				</Box>

				{/* Total row */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						backgroundColor: totalRowConfig.bg,
						px: 2.5,
						py: 2,
						mt: 2,
						transition: "background-color 0.2s ease",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						<Box
							sx={{
								width: 67,
								height: 67,
								borderRadius: 1.5,
								backgroundColor: "rgba(255,255,255,0.15)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<AccountBalanceWalletOutlinedIcon sx={{ fontSize: 45, color: "#fff" }} />
						</Box>
						<Box>
							<Typography
								sx={{
									fontSize: 15,
									fontWeight: 700,
									color: "#fff",
									letterSpacing: "0.06em",
									textTransform: "uppercase",
								}}
							>
								{totalRowConfig.title}
							</Typography>
							<Typography
								sx={{
									fontSize: 13,
									color: "#fff",
								}}
							>
								{totalRowConfig.subtitle}
							</Typography>
						</Box>
					</Box>

					<Stack alignItems={"flex-end !important"} pr={1}>
						<Typography sx={{ fontSize: 11, color: "#fff" }}>{totalRowConfig.statusLabel}</Typography>
						<Box
							sx={{
								backgroundColor: "rgba(255,255,255,0.15)",
								borderRadius: 1.5,
								px: 3,
								py: 0.75,
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								width: "fit-content",
							}}
						>
							<Typography
								sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 600, color: "#fff", letterSpacing: "0.06em", lineHeight: 1.1 }}
							>
								${total_agent_checkout.toLocaleString()}
							</Typography>
						</Box>

						{/* Status chip */}
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								backgroundColor: totalRowConfig.statusBg,
								border: `1px solid ${totalRowConfig.statusBorder}`,
								borderRadius: 5,
								px: 1.25,
								py: 0.4,
								mt: 0.75,
								width: "fit-content",
							}}
						>
							<totalRowConfig.StatusIcon sx={{ fontSize: 13, color: totalRowConfig.statusIconColor }} />
							<Typography
								sx={{
									fontSize: 11,
									fontWeight: 600,
									color: totalRowConfig.statusTextColor,
									lineHeight: 1,
								}}
							>
								{totalRowConfig.statusText}
							</Typography>
						</Box>
					</Stack>
				</Box>
				{/* Slide-in override breakdown overlay */}
				{showBreakdown && (
					<OverrideBreakdownPanel
						override_l1={override_l1}
						override_l2={override_l2}
						override_l3={override_l3}
						override_total={override_total}
						leadership_bonus={leadership_bonus}
						milestone_achieved={milestone_achieved}
						override_percentage={override_percentage}
						network_residue_total_income={network_residue_total_income}
						onClose={() => setShowBreakdown(false)}
					/>
				)}
			</Box>
		</Box>
	);
}

// ─── Main export ──────────────────────────────────────────────────────────────
export default function Cards({ stats = {}, isAgent, selectedMonth, onOverridesMonthChange }) {
	const overridesMonth = selectedMonth ?? dayjs();

	const handleOverridesMonth = (newVal) => {
		onOverridesMonthChange?.(newVal);
	};
	const {
		active_members = 0,
		monthly_service_revenue = 0,
		network_members = 0,
		l1_agents = 0,
		l2_agents = 0,
		l3_agents = 0,
		override_l1 = 0,
		override_l2 = 0,
		override_l3 = 0,
		override_total = 0,
		leadership_bonus = 0,
		total_agent_checkout = 0,
		qualification_status = "N/A",
		override_status = "N/A",
		is_activated = true,
		has_period_data = true,
		period_label = "",
	} = stats;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 4, width: "100%" }}>
			{/* ── ROW 1: Personal Book + Leadership Bonuses ── */}
			<Stack direction={{ xs: "column", lg: "row" }} gap={{ xs: 3, lg: 5 }} mb={{ xs: 0, sm: 3 }}>
				{/* Qualification Status */}
				<Box flex={0.8} width={"100%"}>
					<SectionHeader icon={VerifiedOutlinedIcon} title={isAgent ? "My Status" : "Agent Status"} />
					<QualificationStatusCard
						qualification_status={qualification_status}
						override_status={override_status}
						is_activated={is_activated}
						has_period_data={has_period_data}
						period_label={period_label}
						href={"/overrides"}
					/>
				</Box>

				{/* MY PERSONAL BOOK */}
				<Box flex={1} width={"100%"}>
					<SectionHeader icon={MenuBookIcon} title={isAgent ? "My Personal Book" : "Agent Personal Book"} />
					<CardRow
						cards={[
							{
								label: "Active Members",
								href: "/client-list-2",
								value: active_members,
								stats: stats.active_members_percent,
								Icon: PersonIcon,
								iconBg: "#E6F1FB",
								iconColor: "#185FA5",
								accentColor: "#185FA5",
							},
						]}
					/>
				</Box>

				{/* LEADERSHIP BONUSES */}
				<Box flex={{ xs: 1, lg: 0.8 }} width={"100%"}>
					<SectionHeader icon={WorkspacePremium} title={isAgent ? "Leadership Bonuses" : "Agent Leadership Bonuses"} />
					<Box sx={{ display: "flex", gap: 2, width: "100%" }}>
						<StatCard
							href="/leadership-bonuses"
							label="Total Bonus Earned"
							stats={stats.leadership_bonus_percent}
							value={leadership_bonus}
							Icon={EmojiEventsIcon}
							iconBg="#FFF0E0"
							iconColor="#C05A00"
							accentColor="#C05A00"
							prefix="$"
						/>
					</Box>
				</Box>
			</Stack>

			{/* ── ROW 2: Network Book + Monthly Overrides + Total Checkout ── */}
			<Stack direction={{ xs: "column", lg: "row" }} gap={{ xs: 3, lg: 5 }}>
				{/* MY NETWORK BOOK */}
				<Box flex={1} width={"100%"}>
					<SectionHeader icon={AccountTree} title={isAgent ? "My Network Book" : "Agent Network Book"} />
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr", md: "1fr" },
							gap: 2,
							width: "100%",
						}}
					>
						{[
							{
								label: "L1 Agents",
								href: "/networks?filter=active&level=l1",
								value: l1_agents,
								Icon: SupervisorAccountIcon,
								iconBg: "#E0F7FA",
								iconColor: "#00838F",
								accentColor: "#00ACC1",
							},
							{
								label: "L2 Agents",
								href: "/networks?filter=active&level=l2",
								value: l2_agents,
								Icon: AccountTreeIcon,
								iconBg: "#E3F2FD",
								iconColor: "#1565C0",
								accentColor: "#1E88E5",
							},
							{
								label: "L3 Agents",
								href: "/networks?filter=active&level=l3",
								value: l3_agents,
								Icon: AccountTreeIcon,
								iconBg: "#F3E5F5",
								iconColor: "#6A1B9A",
								accentColor: "#8E24AA",
							},
							{
								label: "Network Members",
								href: "/networks",
								value: network_members,
								stats: stats.network_members_percent,
								Icon: PeopleAltIcon,
								iconBg: "#E6F1FB",
								iconColor: "#185FA5",
								accentColor: "#185FA5",
							},
						].map((card) => (
							<StatCard key={card.label} {...card} />
						))}
					</Box>
				</Box>

				{/* TOTAL CHECKOUT */}
				<TotalCheckoutBox
					monthly_service_revenue={monthly_service_revenue}
					override_total={override_total}
					override_l1={override_l1}
					override_l2={override_l2}
					override_l3={override_l3}
					total_agent_checkout={total_agent_checkout}
					leadership_bonus={stats.leadership_bonus_earned_this_month}
					override_percentage={stats.override_percentage}
					title={"Payment Summary"}
					month={<MonthPicker value={overridesMonth} onChange={handleOverridesMonth} />}
					active_members={active_members}
					network_residue_total_income={stats.network_residue_total_income}
					milestone_achieved={stats.milestone_achieved}
					isAgent={isAgent}
				/>
			</Stack>
		</Box>
	);
}
